<template>
    <div class="fixed top-0 left-0 z-20 w-full h-full overflow-y-auto bg-white outline-none" id="form-step-4">
        <div class="container flex items-center justify-center h-screen mx-auto">
            <div class="p-8 mx-4 border-2 rounded-lg shadow-md md:mx-auto sm:w-75 md:w-50">
                <div class="text-center">
                    <img :src="logoSrc" alt="Unbundled Legal Help" class="mx-auto img-responsive">
                </div>

                <div class="flex flex-row items-center justify-between pt-8">
                    <div>
                        <i class="fas fa-sync fa-spin fa-3x"></i>
                    </div>

                    <div class="pl-8">
                        <h3 class="text-xl md:text-2xl">
                            Dear {{ name }},
                        </h3>

                        <span class="text-sm md:text-base lg:text-lg">
                            Just a moment while we connect you with an attorney that serves your local area&#8230;
                        </span>

                        <p v-if="link" class="text-sm md:text-base lg:text-lg mt-2">If not automatically redirected, <a class="text-brand-blue underline hover:text-brand-blue-dark" :href="link">click here</a> to view your provider details.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const REDIRECT_TIMEOUT = 1000;

export default {
    props: {
        logoSrc: {
            type: String,
            required: true
        },
        payload: {
            type: Object,
            required: true
        },
        lead: {
            type: Object,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        sector: {
            type: String,
            required: true
        },
        location: {
            type: String,
            required: true
        },
        link: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            interval: false,
        };
    },
    mounted() {
        let increment = 0;
        this.interval = setInterval(() => {
            if (this.checkGTMLoaded() || increment > 2) {
                this.processConversion();
                clearInterval(this.interval);
            }
            increment++;
        }, 500);
    },
    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = false;
        }
    },
    methods: {
        async processConversion() {
            this.pushOpenAIConversionEvent(this.lead);
            this.pushDataLayerEvents(this.lead, this.link);
            this.addMetaRefreshHeader(this.link);

            setTimeout(() => {
                this.redirect(this.link);
            }, REDIRECT_TIMEOUT + (REDIRECT_TIMEOUT * 0.1));
        },

        /**
         * @param {string} redirectUrl
         * @returns {void}
         */
        redirect (redirectUrl) {
            if (! this.shouldRedirectAfterSubmission()) {
                return;
            }

            window.setTimeout(() => {
                window.location.href = redirectUrl;
            }, 100);
        },

        /**
         * @param {object} lead
         * @param {string} redirectUrl
         * @returns {void}
         */
        pushDataLayerEvents (lead, redirectUrl) {
            const eventName = lead.should_report_purchase
                ? 'purchase'
                : 'generate_lead';

            window.dataLayer.push({
                user_id: lead.account_id,
                ga_client_id: window.SHARED.gaClientId,
                ga_session_id: window.SHARED.gaSessionId,
                intake_capture_id: this.payload.intake_capture_id || null,
                intake_version: this.payload.intake_version,
                intake_method: this.payload.intake_method,
                intake_areaoflaw_agent: this.payload.intake_areaoflaw_agent || null,
                intake_jurisdiction_agent: this.payload.intake_jurisdiction_agent || null,
                lead_id: lead.id,
                lead_type: lead.type,
                provider_id: lead.tenant.id,
                provider_name: lead.tenant.name_formatted,
                division_id: lead.division.id,
                division_name: lead.division.name,
                sector_id: lead.sector.id,
                sector_name: lead.sector.name,
                region_id: lead.region.id,
                region_name: lead.region.name,
                ecommerce: null // Populated via GTM
            });
            window.dataLayer.push({
                event: eventName,
                ecommerce: {
                    transaction_id: lead.key,
                    value: lead.cost_formatted,
                    tax: 0,
                    shipping: 0,
                    currency: 'USD',
                    items: [
                        {
                            item_id: `ULH-${lead.sku}`,
                            item_name: `ULH ${lead.division.name} ${lead.region.country.alpha2}`,
                            affiliation: 'Access Legal',
                            item_brand: 'ULH',
                            item_category: 'Leads',
                            item_category2: lead.division.name,
                            item_category3: lead.sector.name,
                            item_category4: `${lead.region.name}, ${lead.region.state.abbreviation} ${lead.region.country.alpha2}`,
                            item_category5: lead.tenant.id ? `Provider ${lead.tenant.id}` : null,
                            item_variant: `${lead.type_name} ${lead.id}`,
                            price: lead.cost_formatted,
                            quantity: 1
                        }]
                },
                eventTimeout: REDIRECT_TIMEOUT,
                eventCallback: () => {
                    this.redirect(redirectUrl);
                },
            });
        },

        checkGTMLoaded() {
            if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
                return false;
            }

            let gtmStartedEvent = window.dataLayer.find(element => element['gtm.start']);

            return (gtmStartedEvent && gtmStartedEvent['gtm.uniqueEventId']);
        },

        /**
         * @param {string} redirectUrl
         * @returns {void}
         */
        addMetaRefreshHeader (redirectUrl) {
            let meta = document.createElement('meta');
            meta.setAttribute('http-equiv', 'refresh');
            // REDIRECT_TIMEOUT is in ms, this meta tag expects seconds
            meta.setAttribute('content', `${REDIRECT_TIMEOUT / 1000};url=${redirectUrl}`);
            document.getElementsByTagName('head')[0].appendChild(meta);
        },

        shouldRedirectAfterSubmission() {
            return !(window.autoRedirectAfterSubmission === false);
        },

        /**
         * @param {object} lead
         * @returns {void}
         */
        pushOpenAIConversionEvent(lead) {
            if (typeof window === 'undefined' || typeof window.oaiq !== 'function') {
                return;
            }

            const eventName = lead.should_report_purchase
                ? 'purchase'
                : 'generate_lead';

            try {
                window.oaiq(
                    'measure',
                    'custom',
                    {
                        type: 'custom',
                    },
                    {
                        event_id: `${lead.type}_${lead.key}`,
                        custom_event_name: eventName,
                    },
                );
            } catch (error) {
                console.error('[ULH] OpenAI conversion event failed', error);
            }
        },
    }
};
</script>
